import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import type { CardPaperType } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { TaylorAttributeReader } from './taylor.readers.types';
import { getPossibleOptions } from './utils';
import { getFoilConfigs } from './useDeriveFoilIds';
import { findFoilLayer } from '@apps/card/utils';
import { isFoilTemplate } from '@apps/card/utils/isFoilTemplate';

const cardPaperTypeToOptionId = (paperType: CardPaperType): string => {
  switch (paperType) {
    case 'standard': {
      return 'signature';
    }
    default: {
      return paperType;
    }
  }
};

/** Pure getters of `useTaylorMappings`, in registration order. */
export const readTaylorAttributes = (product: StationeryProduct, themeId: string): TaylorAttributeReader[] => {
  // Foil colours are only offered on foil templates; every other template is pinned to 'none'.
  // A foil template never falls back to 'none', so its foil is always charged.
  const isFoil = isFoilTemplate({ themeId });
  const foilConfigs = getFoilConfigs(product, 'foil');

  return [
    {
      productAttributeId: 'paperType',
      getOptionId: (draftData: DraftJson) => cardPaperTypeToOptionId(draftData.customizations.paperType),
      allowedOptionIDs: getPossibleOptions(product, 'paperType'),
    },
    {
      productAttributeId: 'shape',
      getOptionId: (draftData: DraftJson) => draftData.customizations.shape,
      allowedOptionIDs: getPossibleOptions(product, 'shape'),
    },
    {
      productAttributeId: 'returnAddress',
      getOptionId: (draftData: DraftJson) => draftData.card.envelope.layout,
      allowedOptionIDs: getPossibleOptions(product, 'returnAddress'),
    },
    {
      productAttributeId: 'recipientAddress',
      getOptionId: (draftData: DraftJson) => draftData.card.envelope.recipientAddressLayout ?? 'none',
      allowedOptionIDs: getPossibleOptions(product, 'recipientAddress'),
    },
    {
      productAttributeId: 'foil',
      getOptionId: (draftData: DraftJson) => {
        if (!isFoil) {
          return 'none';
        }
        const foilColorId = findFoilLayer(draftData.card.front.layers)?.foilData?.colorId;
        return foilConfigs?.find(f => f.colorId === foilColorId)?.optionId ?? 'gold';
      },
      allowedOptionIDs: getPossibleOptions(product, 'foil').filter(optionId => (isFoil ? optionId !== 'none' : optionId === 'none')),
    },
    {
      productAttributeId: 'cardBack',
      getOptionId: () => 'free',
      allowedOptionIDs: ['free'],
    },
  ];
};
